import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, X, FolderOpen, ChevronLeft, Pause, Play, Archive, Pencil, Layers,
  MapPin, CalendarClock, Activity, Target, CheckCircle2,
} from "lucide-react";

const STATUS_STYLE = {
  Active: { bg: "rgba(34,197,94,.15)", color: "#22c55e" },
  Paused: { bg: "rgba(245,158,11,.15)", color: "#f59e0b" },
  Closed: { bg: "rgba(113,113,122,.2)", color: "#a1a1aa" },
};

// Batch 8D · controlled outcome vocabularies + patient/clinician labels.
const CLOSURE_REASONS = [
  ["completed_rehab", "Completed rehabilitation"],
  ["self_management", "Progressed to self-management"],
  ["maintenance", "Maintenance"],
  ["referred_on", "Referred on"],
  ["patient_choice", "Patient choice"],
  ["lost_to_follow_up", "Lost to follow-up"],
  ["administrative", "Administrative"],
  ["other", "Other"],
];
const GOAL_OUTCOMES = [
  ["achieved", "Achieved"],
  ["partially_achieved", "Partially achieved"],
  ["not_achieved", "Not achieved"],
  ["not_assessed", "Not assessed"],
  ["follow_up_declined", "Follow-up declined"],
];
const CLOSURE_LABEL = Object.fromEntries(CLOSURE_REASONS);
const GOAL_LABEL = Object.fromEntries(GOAL_OUTCOMES);

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Closed;
  return (
    <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full"
          style={{ background: s.bg, color: s.color }} data-testid="case-status-badge">
      {status}
    </span>
  );
}

/* ================================================================
 * PatientCases — Batch 4 · Cases/Episodes management for one patient.
 * Patient → Case → Programme(s). Supports multiple simultaneous active
 * cases, Add-alongside vs Replace, and case lifecycle (pause/close/reopen).
 * ================================================================ */
export default function PatientCases({ patientId, patientName, library = [], onChanged, initialCaseId = null }) {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  const [newCaseOpen, setNewCaseOpen] = useState(false);
  const [newCase, setNewCase] = useState({ title: "", body_region: "", clinical_summary: "" });
  const [editCaseOpen, setEditCaseOpen] = useState(false);
  const [editCase, setEditCase] = useState({ title: "", body_region: "", clinical_summary: "" });
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeForm, setCloseForm] = useState({ closure_reason: "", goal_outcome: "", follow_up_declined: false, patient_summary: "" });
  const [outcomeEditOpen, setOutcomeEditOpen] = useState(false);
  const [outcomeForm, setOutcomeForm] = useState({ closure_reason: "", goal_outcome: "", follow_up_declined: false, patient_summary: "" });

  const loadCases = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${patientId}/cases`);
      setCases(Array.isArray(data) ? data : []);
    } catch { toast.error("Could not load cases"); }
    finally { setLoading(false); }
  };
  const loadDetail = async (caseId) => {
    try {
      const { data } = await api.get(`/cases/${caseId}`);
      setDetail(data);
    } catch { toast.error("Could not load case"); }
  };

  useEffect(() => { loadCases(); }, [patientId]);
  // Batch 9A · preselect a Case from the deep-link ?case= param (once it loads).
  useEffect(() => {
    if (initialCaseId && !selectedId && cases.some((c) => c.id === initialCaseId)) {
      setSelectedId(initialCaseId);
    }
  }, [initialCaseId, cases]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedId) loadDetail(selectedId); else setDetail(null); }, [selectedId]);

  const createCase = async () => {
    if (!newCase.title.trim()) return toast.error("Case title is required");
    try {
      const { data } = await api.post(`/patients/${patientId}/cases`, newCase);
      toast.success("Case created");
      setNewCaseOpen(false);
      setNewCase({ title: "", body_region: "", clinical_summary: "" });
      await loadCases();
      setSelectedId(data.id);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create case"); }
  };

  const saveEditCase = async () => {
    if (!editCase.title.trim()) return toast.error("Case title is required");
    try {
      await api.patch(`/cases/${selectedId}`, editCase);
      toast.success("Case updated");
      setEditCaseOpen(false);
      await loadCases(); await loadDetail(selectedId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update case"); }
  };

  const lifecycle = async (action) => {
    try {
      await api.post(`/cases/${selectedId}/${action}`);
      toast.success(`Case ${action === "reopen" ? "reopened" : action + "d"}`);
      await loadCases(); await loadDetail(selectedId);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const doCloseCase = async () => {
    if (!closeForm.closure_reason) return toast.error("Select a reason for closure");
    if (!closeForm.goal_outcome) return toast.error("Select a goal outcome");
    try {
      await api.post(`/cases/${selectedId}/close`, closeForm);
      toast.success("Case closed");
      setCloseOpen(false);
      await loadCases(); await loadDetail(selectedId);
      onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to close case"); }
  };

  const saveOutcomeEdit = async () => {
    if (!outcomeForm.closure_reason) return toast.error("Select a reason for closure");
    if (!outcomeForm.goal_outcome) return toast.error("Select a goal outcome");
    try {
      await api.patch(`/cases/${selectedId}/outcome`, outcomeForm);
      toast.success("Outcome updated");
      setOutcomeEditOpen(false);
      await loadDetail(selectedId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update outcome"); }
  };

  const activeCases = cases.filter((c) => c.status === "Active");
  const otherCases = cases.filter((c) => c.status !== "Active");

  /* ---------------- Case detail view ---------------- */
  if (selectedId && detail) {
    const S = STATUS_STYLE[detail.status] || STATUS_STYLE.Closed;
    return (
      <div className="fl-card fl-card-elevated p-6 mt-6" data-testid="case-detail">
        {/* Breadcrumb + back */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-zinc-500 flex items-center gap-1.5 flex-wrap" data-testid="case-breadcrumb">
            <span className="truncate max-w-[12rem]">{patientName || "Patient"}</span>
            <span className="text-zinc-600">›</span>
            <span>Cases &amp; Programmes</span>
            <span className="text-zinc-600">›</span>
            <span className="text-[#f47a20] font-semibold truncate max-w-[12rem]">{detail.title}</span>
          </div>
          <button onClick={() => setSelectedId(null)} className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1" data-testid="case-back">
            <ChevronLeft size={14} /> Back to all cases
          </button>
        </div>

        {/* Strong Case header */}
        <div className="mt-3 rounded-xl border-l-4 p-4" style={{ borderColor: S.color, background: "rgba(255,255,255,0.02)" }} data-testid="case-detail-header">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="fl-heading text-2xl uppercase" data-testid="case-detail-title">{detail.title}</h3>
              <div className="flex items-center gap-2 mt-1.5 text-xs text-zinc-400">
                <StatusBadge status={detail.status} />
                {detail.body_region && <span>· {detail.body_region}</span>}
                <span>· Opened {detail.opened_at ? new Date(detail.opened_at).toLocaleDateString("en-GB") : "—"}</span>
              </div>
              {detail.clinical_summary && <div className="text-sm text-zinc-400 mt-2 max-w-2xl">{detail.clinical_summary}</div>}
            </div>
            <div className="flex flex-wrap gap-2">
              <button className="fl-btn-ghost text-xs" onClick={() => { setEditCase({ title: detail.title || "", body_region: detail.body_region || "", clinical_summary: detail.clinical_summary || "" }); setEditCaseOpen(true); }} data-testid="case-edit-btn"><Pencil size={12} /> Edit</button>
              {detail.status === "Active" && <button className="fl-btn-ghost text-xs" onClick={() => lifecycle("pause")} data-testid="case-pause-btn"><Pause size={12} /> Pause</button>}
              {detail.status !== "Active" && <button className="fl-btn-ghost text-xs" onClick={() => lifecycle("reopen")} data-testid="case-reopen-btn"><Play size={12} /> Reopen</button>}
              {detail.status !== "Closed" && <button className="fl-btn-ghost text-xs" onClick={() => { setCloseForm({ closure_reason: "", goal_outcome: "", follow_up_declined: false, patient_summary: "" }); setCloseOpen(true); }} data-testid="case-close-btn"><Archive size={12} /> Close</button>}
            </div>
          </div>
        </div>

        {editCaseOpen && (
          <div className="mt-4 border border-[#f47a20]/30 rounded-lg p-4 space-y-2" data-testid="case-edit-form">
            <input className="fl-input" placeholder="Case title" value={editCase.title} onChange={(e) => setEditCase({ ...editCase, title: e.target.value })} data-testid="case-edit-title" />
            <input className="fl-input" placeholder="Body region (optional)" value={editCase.body_region} onChange={(e) => setEditCase({ ...editCase, body_region: e.target.value })} />
            <textarea className="fl-input" rows={2} placeholder="Short clinical summary (optional)" value={editCase.clinical_summary} onChange={(e) => setEditCase({ ...editCase, clinical_summary: e.target.value })} />
            <div className="flex gap-2"><button className="fl-btn text-xs" onClick={saveEditCase} data-testid="case-edit-save">Save</button><button className="fl-btn-ghost text-xs" onClick={() => setEditCaseOpen(false)}>Cancel</button></div>
          </div>
        )}

        {/* Batch 8D · Close Case dialog */}
        {closeOpen && (
          <div className="mt-4 border border-[#f47a20]/40 rounded-lg p-4 space-y-3" data-testid="case-close-form">
            <div className="fl-heading text-base">Close case · {detail.title}</div>
            <div>
              <div className="fl-label mb-1">Reason for closure · required</div>
              <select className="fl-input" value={closeForm.closure_reason} onChange={(e) => setCloseForm({ ...closeForm, closure_reason: e.target.value })} data-testid="case-close-reason">
                <option value="">— Select a reason —</option>
                {CLOSURE_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <div className="fl-label mb-1">Goal outcome · required</div>
              <select className="fl-input" value={closeForm.goal_outcome} onChange={(e) => setCloseForm({ ...closeForm, goal_outcome: e.target.value })} data-testid="case-close-goal">
                <option value="">— Select an outcome —</option>
                {GOAL_OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="case-close-followup-label">
              <input type="checkbox" checked={closeForm.follow_up_declined} onChange={(e) => setCloseForm({ ...closeForm, follow_up_declined: e.target.checked })} data-testid="case-close-followup" />
              Patient declined further follow-up
            </label>
            <div>
              <div className="fl-label mb-1">Patient summary · <span className="text-[#f8a766]">visible to patient</span> · optional</div>
              <textarea className="fl-input" rows={3} placeholder="e.g. You have returned to regular running without significant symptoms and are continuing independently with your strength programme." value={closeForm.patient_summary} onChange={(e) => setCloseForm({ ...closeForm, patient_summary: e.target.value })} data-testid="case-close-summary" />
            </div>
            <div className="flex gap-2"><button className="fl-btn text-xs" onClick={doCloseCase} data-testid="case-close-confirm"><Archive size={12} /> Close case</button><button className="fl-btn-ghost text-xs" onClick={() => setCloseOpen(false)}>Cancel</button></div>
          </div>
        )}

        {/* Batch 8D · Case outcome (read-only) when Closed */}
        {detail.status === "Closed" && detail.outcome && (
          <div className="mt-4 rounded-xl border border-white/10 p-4" data-testid="case-outcome">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#a1a1aa]" /><div className="fl-label text-xs">Case outcome</div></div>
              <button className="fl-btn-ghost text-[11px]" onClick={() => { setOutcomeForm({ closure_reason: detail.outcome.closure_reason || "", goal_outcome: detail.outcome.goal_outcome || "", follow_up_declined: !!detail.outcome.follow_up_declined, patient_summary: detail.outcome.patient_summary || "" }); setOutcomeEditOpen(true); }} data-testid="case-outcome-edit-btn"><Pencil size={11} /> Edit outcome</button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              <div><div className="fl-label">Reason for closure</div><div className="text-zinc-200 mt-1" data-testid="case-outcome-reason">{CLOSURE_LABEL[detail.outcome.closure_reason] || detail.outcome.closure_reason || "—"}</div></div>
              <div><div className="fl-label">Goal outcome</div><div className="text-zinc-200 mt-1" data-testid="case-outcome-goal">{GOAL_LABEL[detail.outcome.goal_outcome] || detail.outcome.goal_outcome || "—"}</div></div>
              <div><div className="fl-label">Closed</div><div className="text-zinc-200 mt-1">{detail.closed_at ? new Date(detail.closed_at).toLocaleDateString("en-GB") : "—"}</div></div>
            </div>
            {detail.outcome.follow_up_declined && <div className="mt-2 text-xs text-amber-400/90" data-testid="case-outcome-followup">Patient declined further follow-up</div>}
            {detail.outcome.patient_summary && (
              <div className="mt-3 text-sm"><div className="fl-label mb-1">Summary visible to patient</div><div className="text-zinc-300" data-testid="case-outcome-summary">{detail.outcome.patient_summary}</div></div>
            )}
            {detail.outcome.edited_at && <div className="mt-2 text-[11px] text-zinc-500">Outcome corrected {new Date(detail.outcome.edited_at).toLocaleDateString("en-GB")}{detail.outcome.edited_by_name ? ` by ${detail.outcome.edited_by_name}` : ""}</div>}
          </div>
        )}

        {/* Batch 8D · Edit outcome dialog */}
        {outcomeEditOpen && (
          <div className="mt-4 border border-[#f47a20]/40 rounded-lg p-4 space-y-3" data-testid="case-outcome-edit-form">
            <div className="fl-heading text-base">Correct outcome · {detail.title}</div>
            <div>
              <div className="fl-label mb-1">Reason for closure · required</div>
              <select className="fl-input" value={outcomeForm.closure_reason} onChange={(e) => setOutcomeForm({ ...outcomeForm, closure_reason: e.target.value })} data-testid="case-outcome-edit-reason">
                {CLOSURE_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <div className="fl-label mb-1">Goal outcome · required</div>
              <select className="fl-input" value={outcomeForm.goal_outcome} onChange={(e) => setOutcomeForm({ ...outcomeForm, goal_outcome: e.target.value })} data-testid="case-outcome-edit-goal">
                {GOAL_OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={outcomeForm.follow_up_declined} onChange={(e) => setOutcomeForm({ ...outcomeForm, follow_up_declined: e.target.checked })} data-testid="case-outcome-edit-followup" />
              Patient declined further follow-up
            </label>
            <div>
              <div className="fl-label mb-1">Patient summary · <span className="text-[#f8a766]">visible to patient</span></div>
              <textarea className="fl-input" rows={3} value={outcomeForm.patient_summary} onChange={(e) => setOutcomeForm({ ...outcomeForm, patient_summary: e.target.value })} data-testid="case-outcome-edit-summary" />
            </div>
            <div className="flex gap-2"><button className="fl-btn text-xs" onClick={saveOutcomeEdit} data-testid="case-outcome-edit-save">Save outcome</button><button className="fl-btn-ghost text-xs" onClick={() => setOutcomeEditOpen(false)}>Cancel</button></div>
          </div>
        )}

        <CaseRecoverySummary patientId={patientId} caseId={detail.id} />

        <ProgrammeSection
          patientId={patientId}
          caseDoc={detail}
          library={library}
          onSaved={async () => { await loadCases(); await loadDetail(selectedId); onChanged && onChanged(); }}
        />
      </div>
    );
  }

  /* ---------------- Cases overview ---------------- */
  return (
    <div className="fl-card fl-card-elevated p-6 mt-6" data-testid="patient-cases">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Layers className="text-[#f47a20]" size={18} />
          <h3 className="fl-heading text-xl">Cases &amp; programmes</h3>
        </div>
        <button className="fl-btn text-xs" onClick={() => setNewCaseOpen(true)} data-testid="case-new-btn"><Plus size={12} /> New case</button>
      </div>

      {newCaseOpen && (
        <div className="mb-4 border border-[#f47a20]/30 rounded-lg p-4 space-y-2" data-testid="case-new-form">
          <div className="fl-label mb-1">New case / episode</div>
          <input className="fl-input" placeholder="Case title · e.g. Left ACL rehabilitation" value={newCase.title} onChange={(e) => setNewCase({ ...newCase, title: e.target.value })} data-testid="case-new-title" />
          <input className="fl-input" placeholder="Body region (optional)" value={newCase.body_region} onChange={(e) => setNewCase({ ...newCase, body_region: e.target.value })} data-testid="case-new-region" />
          <textarea className="fl-input" rows={2} placeholder="Short clinical summary (optional)" value={newCase.clinical_summary} onChange={(e) => setNewCase({ ...newCase, clinical_summary: e.target.value })} />
          <div className="flex gap-2"><button className="fl-btn text-xs" onClick={createCase} data-testid="case-new-save">Create case</button><button className="fl-btn-ghost text-xs" onClick={() => setNewCaseOpen(false)}>Cancel</button></div>
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading cases…</div>
      ) : cases.length === 0 ? (
        <div className="text-zinc-500 text-sm" data-testid="cases-empty">No cases yet. Create a case to start building rehabilitation programmes.</div>
      ) : (
        <>
          <div className="fl-label mb-2">Active cases</div>
          {activeCases.length === 0 ? (
            <div className="text-zinc-600 text-sm mb-3">No active cases.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-2">
              {activeCases.map((c) => (
                <button key={c.id} onClick={() => setSelectedId(c.id)} className="fl-card p-4 text-left hover:border-[#f47a20]/40 transition" data-testid={`case-card-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm text-white font-medium truncate">{c.title}</div>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="text-xs text-zinc-500 mt-2 flex items-center gap-1">
                    <FolderOpen size={12} /> {c.active_programme_count} active · {c.programme_count} total
                  </div>
                </button>
              ))}
            </div>
          )}

          {otherCases.length > 0 && (
            <div className="mt-3">
              <button className="text-xs text-zinc-500 hover:text-white" onClick={() => setShowClosed((v) => !v)} data-testid="cases-closed-toggle">
                {showClosed ? "Hide" : "Show"} paused / closed cases ({otherCases.length})
              </button>
              {showClosed && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2" data-testid="cases-closed-list">
                  {otherCases.map((c) => (
                    <button key={c.id} onClick={() => setSelectedId(c.id)} className="fl-card p-4 text-left opacity-70 hover:opacity-100 hover:border-white/20 transition" data-testid={`case-card-${c.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-sm text-white font-medium truncate">{c.title}</div>
                        <StatusBadge status={c.status} />
                      </div>
                      <div className="text-xs text-zinc-500 mt-2">{c.programme_count} programme{c.programme_count !== 1 ? "s" : ""}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ================================================================
 * CaseRecoverySummary — Batch 8B · read-only Shared Recovery Plan summary
 * shown inside the selected Case (what the patient sees for THIS case):
 * patient goal, next appointment, 7-day adherence and the current roadmap
 * phase (deterministic link only). No editing — all edits stay in Roadmap /
 * Programme / Appointment workflows.
 * ================================================================ */
function CaseRecoverySummary({ patientId, caseId }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/patients/${patientId}/recovery`)
      .then((r) => { if (alive) setRec(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [patientId, caseId]);

  if (loading) return <div className="mt-4 text-xs text-zinc-500" data-testid="case-recovery-loading">Loading recovery summary…</div>;
  if (!rec) return null;
  const c = (rec.cases || []).find((x) => x.id === caseId);
  if (!c) return null;

  const rm = c.roadmap?.roadmap;
  let phaseLine = null;
  if (rm) {
    const phases = [...(rm.phases || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = phases.findIndex((p) => p.id === rm.current_phase_id);
    const cur = idx >= 0 ? phases[idx] : null;
    phaseLine = cur ? `Phase ${idx + 1} of ${phases.length} · ${cur.title}` : null;
  }
  const appt = rec.next_appointment;

  return (
    <div className="mt-4 rounded-xl border border-white/10 p-4" data-testid="case-recovery-summary">
      <div className="flex items-center gap-2 mb-3">
        <MapPin size={14} className="text-[#f47a20]" />
        <div className="fl-label text-xs">Recovery plan · what the patient sees</div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="text-sm">
          <div className="flex items-center gap-1.5 fl-label"><Target size={12} className="text-[#f47a20]" /> Patient goal</div>
          <div className="text-zinc-200 mt-1">{rec.goal || "—"}</div>
        </div>
        <div className="text-sm">
          <div className="flex items-center gap-1.5 fl-label"><CalendarClock size={12} className="text-[#f47a20]" /> Next appointment</div>
          <div className="text-zinc-200 mt-1">{appt ? new Date(appt.scheduled_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "None booked"}</div>
        </div>
        <div className="text-sm">
          <div className="flex items-center gap-1.5 fl-label"><Activity size={12} className="text-[#f47a20]" /> 7-day adherence</div>
          <div className="text-zinc-200 mt-1">{rec.adherence}%</div>
        </div>
      </div>
      <div className="mt-3 text-sm" data-testid="case-recovery-roadmap">
        <div className="flex items-center gap-1.5 fl-label mb-1"><MapPin size={12} className="text-[#f47a20]" /> Roadmap phase</div>
        {phaseLine ? (
          <div className="text-zinc-200">{phaseLine}</div>
        ) : (
          <div className="text-zinc-500">No case-specific recovery roadmap has been assigned yet.</div>
        )}
      </div>
    </div>
  );
}

/* ================================================================
 * ProgrammeSection — programmes inside a single case + the builder.
 * ================================================================ */
function ProgrammeSection({ patientId, caseDoc, library, onSaved }) {
  const programmes = caseDoc.programmes || [];
  const activeProgs = programmes.filter((p) => p.active);
  const inactiveProgs = programmes.filter((p) => !p.active);
  const caseClosed = caseDoc.status === "Closed";

  const [builderOpen, setBuilderOpen] = useState(false);
  const [mode, setMode] = useState("add"); // add | replace | edit
  const [targetId, setTargetId] = useState("");
  const [form, setForm] = useState({ title: "", phase: "Rehabilitation", goal: "", reason: "", stage_key: "", exercises: [] });
  const [libQ, setLibQ] = useState("");
  const [libHits, setLibHits] = useState([]);

  useEffect(() => {
    const s = libQ.trim();
    if (!s) { setLibHits([]); return; }
    const t = setTimeout(() => {
      api.get(`/exercises?q=${encodeURIComponent(s)}&limit=40`)
        .then((r) => setLibHits(r.data?.items || []))
        .catch(() => setLibHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [libQ]);

  const openNew = () => {
    setMode(activeProgs.length > 0 ? "add" : "add");
    setTargetId("");
    setForm({ title: "", phase: "Rehabilitation", goal: "", reason: "", stage_key: "", exercises: [] });
    setBuilderOpen(true);
  };
  const openEdit = (p) => {
    setMode("edit");
    setTargetId(p.id);
    setForm({
      title: p.title || "", phase: p.phase || "Rehabilitation", goal: p.goal || "", reason: "", stage_key: p.stage_key || "",
      exercises: (p.exercises || []).map((r) => ({ id: r.id, exercise_id: r.exercise_id, sets: r.sets, reps: r.reps, frequency_per_week: r.frequency_per_week || 3, name: r.exercise_name })),
    });
    setBuilderOpen(true);
  };

  const addExercise = (e) => setForm((f) => ({ ...f, exercises: [...f.exercises, { exercise_id: e.id, sets: e.default_sets, reps: e.default_reps, rest_seconds: 60, notes: "", frequency_per_week: 3, name: e.name }] }));
  const removeExercise = (i) => setForm((f) => ({ ...f, exercises: f.exercises.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title.trim() || form.exercises.length === 0) return toast.error("Add a title and at least one exercise");
    if ((mode === "replace" || mode === "edit") && !form.reason.trim()) return toast.error("A reason for change is required");
    if (mode === "replace" && !targetId) return toast.error("Select which programme to replace");
    try {
      await api.post("/programmes", {
        patient_id: patientId,
        case_id: caseDoc.id,
        mode,
        target_programme_id: (mode === "replace" || mode === "edit") ? targetId : null,
        title: form.title, phase: form.phase, goal: form.goal,
        stage_key: form.stage_key || null,
        reason: form.reason.trim() || null,
        exercises: form.exercises.map(({ name, ...e }) => e),
      });
      toast.success(mode === "add" ? "Programme added" : mode === "replace" ? "Programme replaced" : "Programme updated");
      setBuilderOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save programme"); }
  };

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-3">
        <div className="fl-label">Programmes ({activeProgs.length} active)</div>
        {!caseClosed && <button className="fl-btn text-xs" onClick={openNew} data-testid="prog-new-btn"><Plus size={12} /> New programme</button>}
      </div>
      {caseClosed && <div className="text-xs text-amber-400/80 mb-3">This case is closed. Reopen it to assign new programmes.</div>}

      {programmes.length === 0 ? (
        <div className="text-zinc-500 text-sm">No programmes in this case yet.</div>
      ) : (
        <div className="space-y-2">
          {[...activeProgs, ...inactiveProgs].map((p) => (
            <div key={p.id} className={`flex items-center justify-between gap-3 border border-white/5 rounded-lg p-3 ${p.active ? "" : "opacity-60"}`} data-testid={`prog-row-${p.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-white truncate">{p.title}
                  {p.active
                    ? <span className="ml-2 text-[9px] uppercase px-1.5 py-0.5 rounded" style={{ background: "rgba(34,197,94,.15)", color: "#22c55e" }}>active</span>
                    : <span className="ml-2 text-[9px] uppercase px-1.5 py-0.5 rounded bg-white/10 text-zinc-400">inactive</span>}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{p.phase} · {(p.exercises || []).length} exercises{p.stage_key ? ` · ${p.stage_key}` : ""}</div>
              </div>
              {p.active && !caseClosed && <button className="fl-btn-ghost text-[11px]" onClick={() => openEdit(p)} data-testid={`prog-edit-${p.id}`}>Edit</button>}
            </div>
          ))}
        </div>
      )}

      {builderOpen && (
        <div className="mt-4 border border-[#f47a20]/40 rounded-lg p-4" data-testid="programme-builder">
          <div className="flex items-center justify-between mb-3">
            <div className="fl-heading text-lg">
              {mode === "edit" ? "Edit programme" : "New programme"} · {caseDoc.title}
            </div>
            <button onClick={() => setBuilderOpen(false)} className="fl-btn-ghost text-xs inline-flex items-center gap-1" data-testid="pb-back-to-profile"><ChevronLeft size={14} /> Back to patient profile</button>
          </div>

          {mode !== "edit" && activeProgs.length > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-3" data-testid="prog-mode">
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="radio" name="prog-mode" checked={mode === "add"} onChange={() => setMode("add")} data-testid="prog-mode-add" /> Add alongside
              </label>
              <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                <input type="radio" name="prog-mode" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="prog-mode-replace" /> Replace existing
              </label>
              {mode === "replace" && (
                <select className="fl-input text-xs py-1 w-56" value={targetId} onChange={(e) => setTargetId(e.target.value)} data-testid="prog-replace-target">
                  <option value="">— Select programme to replace —</option>
                  {activeProgs.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              )}
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-3">
            <input className="fl-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="pb-title" />
            <input className="fl-input" placeholder="Phase" value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} />
            <input className="fl-input" placeholder="Goal" value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} />
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="fl-label">Link to recovery stage (optional)</span>
            <select className="fl-input w-64 text-xs" value={form.stage_key} onChange={(e) => setForm({ ...form, stage_key: e.target.value })} data-testid="pb-stage-key">
              <option value="">Not linked · current overall plan</option>
              <option value="S1">S1 · Protection + tolerable motion</option>
              <option value="S2">S2 · Foundations: mobility + control</option>
              <option value="S3">S3 · Strength + unilateral capacity</option>
              <option value="S4">S4 · Impact + multiplanar preparation</option>
              <option value="S5">S5 · Sport-specific capacity</option>
              <option value="S6">S6 · Review</option>
            </select>
          </div>

          {(mode === "replace" || mode === "edit") && (
            <div className="mt-3">
              <div className="fl-label mb-1">Reason for change · required</div>
              <input className="fl-input" placeholder="Why this change? (recorded in history)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="pb-reason" />
            </div>
          )}

          <div className="mt-4">
            <div className="fl-label mb-2">Library — search the full exercise library</div>
            <input className="fl-input mb-2" placeholder="Search name / region / equipment…" value={libQ} onChange={(e) => setLibQ(e.target.value)} data-testid="pb-lib-search" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-56 overflow-auto">
              {(libQ.trim() ? libHits : library).map((e) => {
                const hasVideo = !!(e.video_url || e.video_public_id);
                return (
                  <button key={e.id} onClick={() => addExercise(e)} className="fl-card p-3 text-left hover:border-[#f47a20]/40" data-testid={`lib-add-${e.id}`}>
                    <div className="text-sm">{e.name}</div>
                    <div className="text-xs text-zinc-500 flex items-center gap-1 flex-wrap mt-0.5">
                      <span>{e.category}</span>
                      {e.clinical_review_status && e.clinical_review_status !== "approved" && <span className="text-[9px] uppercase px-1 rounded bg-white/10">{e.clinical_review_status}</span>}
                      <span className="text-[9px] uppercase px-1 rounded" style={{ background: hasVideo ? "rgba(34,197,94,.15)" : "rgba(255,69,0,.15)", color: hasVideo ? "#22c55e" : "#f47a20" }}>{hasVideo ? "video" : "no video"}</span>
                    </div>
                  </button>
                );
              })}
              {libQ.trim() && libHits.length === 0 && <div className="text-xs text-zinc-500 col-span-full py-2">No matches in the library.</div>}
            </div>
          </div>

          <div className="mt-4">
            <div className="fl-label mb-2">Selected ({form.exercises.length})</div>
            {form.exercises.map((e, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-white/5 text-sm">
                <div className="flex-1">{e.name}</div>
                <input type="number" className="fl-input w-16" value={e.sets} onChange={(ev) => { const ex = [...form.exercises]; ex[i].sets = parseInt(ev.target.value) || 0; setForm({ ...form, exercises: ex }); }} />
                <span className="text-zinc-500">×</span>
                <input type="number" className="fl-input w-16" value={e.reps} onChange={(ev) => { const ex = [...form.exercises]; ex[i].reps = parseInt(ev.target.value) || 0; setForm({ ...form, exercises: ex }); }} />
                <select className="fl-input w-28 text-xs" value={e.frequency_per_week || 3} onChange={(ev) => { const ex = [...form.exercises]; ex[i].frequency_per_week = parseInt(ev.target.value); setForm({ ...form, exercises: ex }); }} data-testid={`pb-freq-${i}`}>
                  {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}× / week</option>)}
                  <option value={7}>Every day</option>
                </select>
                <button onClick={() => removeExercise(i)}><X size={14} className="text-zinc-500" /></button>
              </div>
            ))}
          </div>

          <button className="fl-btn mt-4" onClick={save} data-testid="pb-save">
            {mode === "add" ? "Add programme" : mode === "replace" ? "Replace programme" : "Save changes"}
          </button>
        </div>
      )}
    </div>
  );
}
